<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <FileUploadProgressModal v-if="isUploading" :progress="uploadProgress" />
    <!-- 동적 클래스로 성공 화면일 때만 작은 모달 -->
    <div class="modal-content" :class="{ 'modal-content-compact': isCreated }">
      <div class="modal-header">
        <h2 class="text-xl font-bold">새 강의 시작</h2>
        <button @click="$emit('close')" class="close-button">×</button>
      </div>

      <!-- 회의 생성 완료 후에는 폼을 숨기도록 조건부 렌더링 -->
      <form @submit.prevent="handleSubmit" class="modal-form" v-if="!isCreated">
        <!-- 회의 제목 -->
        <div class="form-group">
          <label for="title">강의 제목</label>
          <input 
            type="text" 
            id="title" 
            v-model="formData.title" 
            required
            placeholder="예: 긴급 팀 미팅"
          >
        </div>

        <!-- 장치 설정 -->
        <div class="device-settings">
          <h3 class="text-lg font-semibold mb-3">장치 설정</h3>
          <div class="device-options">
            <label class="device-option">
              <input 
                type="checkbox" 
                v-model="formData.useAudio"
              >
              <span>마이크 사용</span>
            </label>
            <label class="device-option">
              <input 
                type="checkbox" 
                v-model="formData.useVideo"
              >
              <span>카메라 사용</span>
            </label>
          </div>
          <div class="preview-container" v-if="formData.useVideo">
            <video ref="videoPreview" class="video-preview" autoplay muted></video>
          </div>
        </div>

        <!-- 파일 첨부 UI -->
        <div class="form-group">
          <label>강의 자료 첨부</label>
          <!-- 📎 지원 파일 형식 안내 텍스트 추가 -->
          <small class="file-format-info">
            <i class="fas fa-info-circle"></i>
            지원 형식: PPT, PPTX, DOC, DOCX, PDF
          </small>
          <div 
            class="drop-zone"
            @dragover.prevent
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
            :class="{ 'disabled': isSubmitting }"
          >
            <p><i class="fas fa-cloud-upload-alt mr-2"></i> 파일을 드래그하거나 클릭하여 업로드하세요</p>
            <!-- 📎 파일 형식 제한: PPT, Word, PDF만 허용 -->
            <input 
              type="file" 
              ref="fileInput"
              @change="handleFileChange" 
              multiple
              accept=".ppt,.pptx,.doc,.docx,.pdf"
              style="display: none"
              :disabled="isSubmitting"
            />
          </div>
          
          <div class="file-list" v-if="formData.files.length > 0">
            <div v-for="(file, index) in formData.files" :key="index" class="file-item">
              <span>{{ file.name }}</span>
              <button type="button" @click="removeFile(index)" class="remove-file-btn">×</button>
            </div>
          </div>
        </div>

        <!-- 제출 버튼 -->
        <div class="form-actions">
          <button type="button" @click="$emit('close')" class="cancel-button">
            취소
          </button>
          <!-- 버튼 텍스트를 "회의 시작"에서 "회의 생성"으로 변경하여 의미 명확화 -->
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? '강의 생성 중...' : '강의 생성' }}
          </button>
        </div>
      </form>
      
      <!-- 회의 생성 완료 화면 - 백엔드에서 자동 생성된 입장 코드 표시 -->
      <div v-if="isCreated" class="success-content">
        <div class="success-header">
          <h3 class="success-title">강의가 생성되었습니다!</h3>
        </div>

        <!-- 백엔드에서 자동 생성된 8자리 영숫자 입장 코드 표시 영역 -->
        <div class="join-code-section">
          <label class="join-code-label">강의 입장 코드</label>
          <!-- 코드 표시와 복사 버튼이 하나의 연결된 컴포넌트로 보이도록 스타일링 -->
          <div class="join-code-container">
            <!-- 왼쪽: 자동 생성된 입장 코드 표시 (사용자 입력 불가) -->
            <div class="join-code-display">{{ createdJoinCode }}</div>
            <!-- 오른쪽: 클립보드 복사 버튼 - 코드 표시와 시각적으로 연결됨 -->
            <button 
              type="button" 
              @click="copyJoinCode" 
              class="copy-button"
              :class="{ 'copied': copySuccess }"
            >
              <i class="fas fa-copy"></i>
              {{ copySuccess ? '복사됨!' : '복사' }}
            </button>
          </div>
          <small class="join-code-help">이 코드를 참가자에게 전달하세요</small>
        </div>
        
        <!-- 성공 화면 액션 버튼들 -->
        <div class="success-actions">
          <button type="button" @click="$emit('close')" class="cancel-button">
            닫기
          </button>
          <!-- 사용자가 명시적으로 클릭할 때만 회의 페이지로 이동 -->
          <button 
            type="button" 
            @click="goToMeeting" 
            class="submit-button"
          >
            강의로 이동
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, defineEmits } from 'vue'
import { restApiClient, apiClientV1 } from '@/api/apiConfig'
import { useAuth } from '@/composables/useAuth'
import FileUploadProgressModal from './FileUploadProgressModal.vue'

const emit = defineEmits(['close', 'create'])
const { user } = useAuth() // 로그인된 사용자 정보 가져오기

// 회의 생성 완료 상태 관리를 위한 반응형 데이터 추가
const isSubmitting = ref(false)
const isCreated = ref(false)          // 회의 생성 완료 여부
const createdJoinCode = ref('')       // 백엔드에서 자동 생성된 8자리 영숫자 코드
const createdMeetingId = ref('')      // 생성된 회의 ID
const copySuccess = ref(false)        // 복사 성공 피드백 상태
const videoPreview = ref(null)

// 파일 업로드 진행률 상태 추가
const isUploading = ref(false)
const uploadProgress = ref(0)

// 사용자 이름을 저장할 반응형 변수 추가
const userName = ref('')

let stream = null

// 백엔드에서 자동 생성하므로 사용자 입력 불필요
const formData = reactive({
  title: '',
  useAudio: true,
  useVideo: true,
  files: []
})

// 파일 인풋 참조 추가
const fileInput = ref(null)

// 현재 로그인한 사용자의 이름을 가져오는 함수 추가
const fetchCurrentUserName = async () => {
  try {
    const response = await restApiClient.get('/restapi/users/name')
    userName.value = response.name
  } catch (error) {
    console.error('[오류] 사용자 이름 조회 중 오류:', error)
    // API 호출 실패 시 세션 스토리지의 정보를 fallback으로 사용
    userName.value = user.value?.name || '알수없음'
  }
}

// 파일 입력 트리거 메서드 추가
function triggerFileInput() {
  if (!isSubmitting.value) {
    fileInput.value.click()
  }
}

// 파일 변경 핸들러 추가
function handleFileChange(event) {
  addFiles(event.target.files)
  event.target.value = '' // 입력 초기화
}

// 드롭 이벤트 핸들러 추가
function handleDrop(event) {
  addFiles(event.dataTransfer.files)
}

onMounted(async () => {
  // 컴포넌트 마운트 시 사용자 이름을 먼저 가져오기
  await fetchCurrentUserName()
  
  if (formData.useVideo) {
    startVideoPreview()
  }
})

// 📎 파일 추가 메서드 - PPT, Word, PDF 파일만 허용하도록 검증 로직 추가
function addFiles(newFiles) {
  // 허용되는 파일 확장자 목록
  const allowedExtensions = ['.ppt', '.pptx', '.doc', '.docx', '.pdf'];
  
  // 허용되는 MIME 타입 목록 (보안 강화를 위한 이중 검증)
  const allowedMimeTypes = [
    'application/vnd.ms-powerpoint',                                             // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/msword',                                                        // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',   // .docx
    'application/pdf'                                                            // .pdf
  ];
  
  for (let file of newFiles) {
    const fileName = file.name.toLowerCase();
    
    // 1차 검증: 파일 확장자 확인
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    // 2차 검증: MIME 타입 확인 (파일 확장자 변조 방지)
    const hasValidMimeType = allowedMimeTypes.includes(file.type);
    
    // 두 조건을 모두 만족하는 경우에만 파일 추가
    if (hasValidExtension && hasValidMimeType) {
      formData.files.push(file);
    } else {
      // ❌ 지원하지 않는 파일 형식일 경우 사용자에게 알림
      alert(`지원하지 않는 파일 형식입니다: ${file.name}\n지원 형식: PPT, PPTX, DOC, DOCX, PDF`);
    }
  }
}

// 파일 제거 메서드 추가
function removeFile(index) {
  formData.files.splice(index, 1)
}

// 비디오 프리뷰 시작
async function startVideoPreview() {
  if (formData.useVideo && videoPreview.value) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      })
      videoPreview.value.srcObject = stream
    } catch (error) {
      console.error('Failed to access camera:', error)
      formData.useVideo = false
    }
  }
}

// 비디오 프리뷰 정지
function stopVideoPreview() {
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    stream = null
  }
  if (videoPreview.value) {
    videoPreview.value.srcObject = null
  }
}

// 비디오 상태 변경 감지
watch(() => formData.useVideo, (newValue) => {
  if (newValue) {
    startVideoPreview()
  } else {
    stopVideoPreview()
  }
})

onMounted(() => {
  if (formData.useVideo) {
    startVideoPreview()
  }
})

onUnmounted(() => {
  stopVideoPreview()
})

// 회의 생성 후 자동 페이지 이동 방지, 모달 내에서 성공 화면으로 전환
async function handleSubmit() {

  if (!formData.title.trim()) {
    alert('강의 제목을 입력해주세요.');
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    // 현재 로그인된 사용자 ID
    const userId = user.value?.id;
    
    if (!userId) {
      throw new Error('로그인 정보를 찾을 수 없습니다.');
    }
    
    // 1. 먼저 회의 생성 API 호출하여 meetingId, joinCode 받기 - 백엔드에서 UUID 기반 8자리 영숫자 자동 생성
    const response = await restApiClient.post('/restapi/meetings', {
      userId: userId,
      title: formData.title.trim()
    });
    
    // 자동 생성된 meetingId와 joinCode 받기
    const meetingId = response.meetingId;
    const joinCode = response.joinCode; // 백엔드에서 생성된 8자리 영숫자

    // 생성된 회의 정보를 상태에 저장 (성공 화면에서 사용)
    createdMeetingId.value = meetingId;
    createdJoinCode.value = joinCode;

    sessionStorage.setItem('currentMeetingCode', joinCode);
    sessionStorage.setItem('currentMeetingId', meetingId);


    // 2. 회의 ID 획득 후 FastAPI 트랜스크립션 API 호출
    try {
      const fileFormData = new FormData();
      
      for (let file of formData.files) {
        fileFormData.append('files', file);
      }
      
      const userStr = sessionStorage.getItem('user');
      let sessionUserId = null;
      let userLanguage = null;
      
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          sessionUserId = userData.id;
          userLanguage = userData.language;
        } catch (e) {
          console.error('세션 스토리지에서 사용자 정보 파싱 실패:', e);
        }
      }
      
      fileFormData.append('meetingId', meetingId);
      if (sessionUserId) fileFormData.append('userId', sessionUserId);
      if (userLanguage) fileFormData.append('language', userLanguage);
      fileFormData.append('code', joinCode);
      if (formData.title.trim()) fileFormData.append('meeting_name', formData.title.trim());

      // 파일이 있을 때만 프로그레스 모달 표시
      if (formData.files.length > 0) {
        isUploading.value = true;
        uploadProgress.value = 0;
      }
      
      const totalSize = formData.files.reduce((acc, file) => acc + file.size, 0);
      const totalMb = totalSize / (1024 * 1024);
      const estimatedDurationMs = 2000 + totalMb * 2000;
      
      let progressInterval = null;

      try {
        if (formData.files.length > 0) {
          const startTime = Date.now();
          progressInterval = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            const progress = (elapsedTime / estimatedDurationMs) * 100;
            uploadProgress.value = Math.min(99, Math.floor(progress));
          }, 150);
        }

        await apiClientV1.post('/api/v1/uploads', fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (formData.files.length > 0) {
          uploadProgress.value = 100;
        }

      } catch (uploadError) {
        console.error('FastAPI 호출 중 오류 발생:', uploadError);
        // FastAPI 호출 실패해도 회의 시작은 진행되도록 처리
      } finally {
        if (progressInterval) {
          clearInterval(progressInterval);
        }
        // 파일이 있을 때만 업로드 완료 대기 후 모달 닫기
        if (formData.files.length > 0) {
          setTimeout(() => {
            isUploading.value = false;
          }, 500);
        }
      }
    } catch (error) {
       console.error('파일 업로드 과정에서 오류 발생:', error);
    }
    
    // UX 개선: 자동 window.open 제거, 회의 생성 완료 상태로만 전환
    isCreated.value = true;
    
  } catch (error) {
    console.error('강의 생성 중 오류 발생:', error);
    alert(error.messagee || '강의를 시작할 수 없습니다. 다시 시도해주세요.');
  } finally {
    isSubmitting.value = false;
  }
}

// 자동 생성된 입장 코드를 클립보드에 복사하는 함수
async function copyJoinCode() {
  try {
    await navigator.clipboard.writeText(createdJoinCode.value);
    copySuccess.value = true;
    // 2초 후 복사 성공 피드백 제거
    setTimeout(() => {
      copySuccess.value = false;
    }, 2000);
  } catch (error) {
    console.error('복사 실패:', error);
    // 클립보드 API 실패 시 대체 방법 사용
    const textArea = document.createElement('textarea');
    textArea.value = createdJoinCode.value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    copySuccess.value = true;
    setTimeout(() => {
      copySuccess.value = false;
    }, 2000);
  }
}

// 사용자가 명시적으로 "회의로 이동" 버튼을 클릭했을 때만 실행
function goToMeeting() {
  try {
    const meetingId = createdMeetingId.value;
    const joinCode = createdJoinCode.value;
    
    if (!meetingId || !joinCode) {
      throw new Error('강의 정보가 없습니다.');
    }

    // 1. 보안 토큰 생성
    const securityToken = {
      accessKey: crypto.randomUUID(),
      meetingId: meetingId,
      role: 'host',
      userId: user.value?.id || 'guest',
      timestamp: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000),
      checksum: btoa(`${meetingId}_${Date.now()}_${crypto.randomUUID()}`)
    };

    // 2. 세션스토리지에 검증용 토큰 저장 (MeetingRoom에서 비교할 용도)
    try {
      sessionStorage.setItem('meetingSecurityToken', JSON.stringify(securityToken));
      sessionStorage.setItem('currentMeetingCode', joinCode);
      sessionStorage.setItem('currentMeetingId', meetingId);
      console.log('검증용 토큰이 세션스토리지에 저장됨');
    } catch (storageError) {
      console.error('세션스토리지 저장 실패:', storageError);
      throw new Error('브라우저 저장소에 접근할 수 없습니다.');
    }

    // 3. URL에 토큰 포함해서 생성
    const urlParams = new URLSearchParams({
      key: securityToken.accessKey,
      token: btoa(JSON.stringify(securityToken)),
      code: btoa(joinCode),
      userName: btoa(encodeURIComponent(userName.value)) // URL로만 전달
    });
    
    const meetingUrl = `/meeting/${meetingId}?${urlParams.toString()}`;
    
    // 4. 비디오 프리뷰 정지 및 새 탭에서 회의방 열기
    stopVideoPreview();
    
    const newWindow = window.open(meetingUrl, '_blank');
    
    if (!newWindow) {
      throw new Error('팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.');
    }
    
    // 5. 새 탭이 완전히 로드된 후 기존 탭의 세션 정리
    setTimeout(() => {
      try {
        sessionStorage.removeItem('meetingSecurityToken');
        sessionStorage.removeItem('currentMeetingCode');
        sessionStorage.removeItem('currentMeetingId');
        console.log('기존 탭의 세션 스토리지 정리 완료');
      } catch (cleanupError) {
        console.warn('세션 스토리지 정리 중 오류:', cleanupError);
      }
    }, 2000); // 2초 후 정리
    
    emit('close');
    
  } catch (error) {
    console.error('강의로 이동 중 오류 발생:', error);
    alert(error.message || '강의로 이동할 수 없습니다. 다시 시도해주세요.');
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* 기본 모달 (회의 생성 폼) - 크기 유지 */
.modal-content {
  background: white;
  border-radius: 0.5rem;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  transition: all 0.3s ease;
  crollbar-width: none;        /* Firefox */
  -ms-overflow-style: none;     /* IE, Edge */
}
.modal-content::-webkit-scrollbar {
  display: none;                /* Chrome, Safari, Opera */
}

/* 성공 화면 전용 - 작고 컴팩트한 모달 */
.modal-content-compact {
  width: 65% !important;
  max-width: 380px !important;
  max-height: 60vh !important;
  padding: 1.25rem !important;
  border-radius: 0.75rem !important;
  
  /* 모던한 그림자와 배경 효과 */
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  
  /* 모달 슬라이드 인 애니메이션 */
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 모달 슬라이드 인 애니메이션 */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 성공 화면 모달의 호버 효과 */
.modal-content-compact:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 32px 64px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

/* 성공 화면 내용도 더 컴팩트하게 */
.modal-content-compact .success-content {
  gap: 1.25rem;
}

.modal-content-compact .success-header {
  gap: 0.5rem;
}

.modal-content-compact .success-title {
  font-size: 1.25rem;
  margin-bottom: 0.25rem;
  /* 검정색으로 변경 */
  color: #1f2937;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.modal-content-compact .join-code-section {
  margin: 1rem 0;
  gap: 1rem;
}

.modal-content-compact .success-actions {
  margin-top: 1.25rem;
  gap: 1rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.close-button {
  font-size: 1.5rem;
  line-height: 1;
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
}

.form-group input {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
}

.device-settings {
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  padding: 1rem;
}

.device-options,
.meeting-options {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.device-option,
.meeting-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.preview-container {
  width: 100%;
  aspect-ratio: 16/9;
  background: #f1f5f9;
  border-radius: 0.25rem;
  overflow: hidden;
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 0.25rem;
  color: var(--primary);
  border: 1px solid var(--primary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background: rgba(0, 51, 160, 0.05);
}

.submit-button {
  padding: 0.5rem 1rem;
  background: var(--primary);
  color: white;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 51, 160, 0.2);
}

.submit-button:hover:not(:disabled) {
  background: var(--primary-hover);
  box-shadow: 0 4px 12px rgba(0, 51, 160, 0.3);
  transform: translateY(-1px);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 파일 업로드 관련 스타일 */
.drop-zone {
  border: 2px dashed #e2e8f0;
  border-radius: 0.25rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
}

.drop-zone:hover:not(.disabled) {
  border-color: #4f46e5;
  background-color: #f8fafc;
}

.drop-zone.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.file-list {
  margin-top: 1rem;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #f1f5f9;
  border-radius: 0.125rem;
  margin-bottom: 0.5rem;
}

.remove-file-btn {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
}

.remove-file-btn:hover {
  color: #dc2626;
}

/* 파일 형식 안내 텍스트 스타일 */
.file-format-info {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
}

.file-format-info i {
  color: #3b82f6;
  font-size: 0.75rem;
}

/* 회의 생성 완료 화면 스타일 */
.success-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  text-align: center;
}

.success-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.success-icon {
  font-size: 3rem;
}

.success-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin: 0;
}

/* 회의 코드 섹션 전체 레이아웃 - 현대적 중앙 정렬 */
.join-code-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin: 2rem 0;
  padding: 0 1rem;
}

/* 회의 코드 라벨 스타일 - 현대적 타이포그래피 */
.join-code-label {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

/* 도움말 텍스트 스타일 - 세련된 회색톤 */
.join-code-help {
  color: #64748b;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
  font-weight: 500;
  opacity: 0.8;
}

/* 회의 코드 컨테이너 - 글라스모피즘과 네오모피즘 결합 (크기 축소) */
.join-code-container {
  display: flex;
  align-items: stretch;
  gap: 0;
  justify-content: center;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 51, 160, 0.15);
  box-shadow:
    0 12px 16px -4px rgba(0, 51, 160, 0.1),
    0 6px 6px -4px rgba(0, 51, 160, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0.5rem 0;
  position: relative;
  transform: translateZ(0);
}

/* 회의 코드 표시 영역 - 가독성 우선 디자인 (크기 축소) */
.join-code-display {
  font-family: 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
  font-size: 1rem;
  font-weight: 800;
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.05), rgba(146, 205, 220, 0.05));
  border: none;
  border-radius: 0;
  padding: 0.5rem 0.75rem;
  letter-spacing: 0.1em;
  color: var(--primary);
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: all;
  position: relative;
  text-shadow: 0 1px 2px rgba(0, 51, 160, 0.1);
}

/* 세로 구분선 - 현대적 그라디언트 */
.join-code-display::after {
  content: '';
  position: absolute;
  right: 0;
  top: 25%;
  height: 50%;
  width: 2px;
  background: linear-gradient(to bottom, transparent, rgba(0, 51, 160, 0.3), transparent);
  border-radius: 1px;
}

/* 복사 버튼 - 명확한 텍스트와 트렌디한 배경 (크기 축소) */
.copy-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.08), rgba(146, 205, 220, 0.08));
  border: none;
  border-radius: 0;
  color: var(--primary);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 700;
  font-size: 0.75rem;
  min-width:  90px;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
}

/* 복사 버튼 배경 그라디언트 효과 - 더 부드럽게 */
.copy-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 51, 160, 0.08), transparent);
  transition: left 0.6s ease;
  z-index: -1;
}

/* 호버 시 전체 컨테이너 반응 - 부드러운 3D 효과 */
.join-code-container:hover {
  box-shadow:
    0 24px 48px -8px rgba(0, 51, 160, 0.25),
    0 16px 20px -4px rgba(0, 51, 160, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
}

/* 호버 시 복사 버튼 그라디언트 슬라이드 */
.join-code-container:hover .copy-button::before {
  left: 100%;
}

/* 호버 시 코드 표시 영역 반응 - 텍스트 가독성 유지 */
.join-code-container:hover .join-code-display {
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.12), rgba(146, 205, 220, 0.12));
  color: var(--primary-hover);
  text-shadow: 0 2px 4px rgba(0, 51, 160, 0.15);
  transform: scale(1.02);
}

/* 호버 시 복사 버튼 텍스트도 명확하게 */
.join-code-container:hover .copy-button {
  color: var(--primary-hover);
  background: linear-gradient(135deg, rgba(0, 51, 160, 0.15), rgba(146, 205, 220, 0.15));
}

/* 호버 시 세로선 효과 */
.join-code-container:hover .join-code-display::after {
  background: linear-gradient(to bottom, transparent, rgba(0, 51, 160, 0.6), transparent);
  width: 3px;
}

/* 복사 성공 시 현대적 피드백 애니메이션 */
.copy-button.copied {
  animation: modernCopyFeedback 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 현대적 복사 성공 애니메이션 - 아주대학교 블루 계열 */
@keyframes modernCopyFeedback {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.05);
    background: linear-gradient(135deg, rgba(0, 51, 160, 0.25), rgba(146, 205, 220, 0.25));
    color: #ffffff;
  }
  50% {
    transform: scale(1.1);
    background: linear-gradient(135deg, rgba(0, 51, 160, 0.3), rgba(146, 205, 220, 0.3));
    color: #ffffff;
  }
  100% {
    transform: scale(1);
    background: linear-gradient(135deg, rgba(0, 51, 160, 0.08), rgba(146, 205, 220, 0.08));
    color: var(--primary);
  }
}

/* 아이콘 스타일 - 부드러운 회전과 크기 변화 */
.copy-button i {
  font-size: 0.875rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 51, 160, 0.1));
}

/* 호버 시 아이콘 부드러운 회전 효과 */
.join-code-container:hover .copy-button i {
  transform: rotate(12deg) scale(1.1);
  filter: drop-shadow(0 4px 8px rgba(0, 51, 160, 0.2));
}

/* 성공 화면 액션 버튼들 - 현대적 그리드 레이아웃 */
.success-actions {
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

/* 접근성 개선 - 포커스 스타일 */
.copy-button:focus-visible {
  outline: 3px solid rgba(0, 51, 160, 0.5);
  outline-offset: 2px;
}

/* 프리로더 효과 (사실상 사용 안됨) */
.join-code-container.loading {
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}
</style>